<!--
  Read-only public rendering of a published site. The element chain (.wb > .frame > .scroll >
  .site) is reproduced so the shared stylesheet's ancestor selectors match; everything that
  exists only to edit — section toolbars, insert bars, pickers, card-field checkboxes, hero
  size controls, the flip button — is absent.
-->
<div class="wb site-public">
  <div class="frame">
    <div class="scroll">
      <div class="site" [ngClass]="'lk-' + content.look" [ngStyle]="siteStyle">

        <!-- ArtFlow Pro badge. The site is free to the artist; visible attribution is the return.
             Kept small and out of the reading column, and tagged so the channel can actually be
             measured rather than assumed. -->
        <a class="afp-badge" [href]="afpLink('badge')" target="_blank" rel="noopener noreferrer"
           title="This website was made with ArtFlow Pro">
          <img src="assets/images/thumb_AFP-logo-v02-noTagline-800.png" alt="ArtFlow Pro" />
        </a>


        @for (sec of visibleSections; track sec.id) {
          <div class="sec-wrap">
            <div class="sec-body" [style.max-width]="secWidth(sec)">

              @switch (sec.type) {

                @case ('cover') {
                  <div class="cover">
                    <div class="nav">
                      <span class="logo">{{ content.siteTitle }}</span>
                      <span class="links"><a>Work</a><a>About</a><a>Contact</a></span>
                    </div>
                    @if (shows(sec, 'headline')) { <h1 class="hl">{{ sec.data.headline }}</h1> }
                    @if (shows(sec, 'subhead')) { <p class="sub">{{ sec.data.subhead }}</p> }
                    @if (sec.data.button && canEnquire && shows(sec, 'button')) {
                      <button type="button" class="cbtn" (click)="panel.openEnquiry(null)">{{ sec.data.button }}</button>
                    }
                    @if (sec.data.heroImage && shows(sec, 'heroImage')) {
                      <div class="hero" [style.max-width]="heroWidth(sec.data.heroSize)">
                        <img [src]="sec.data.heroImage" [alt]="content.siteTitle" />
                      </div>
                    }
                    @if ((sec.data.promoHeading && shows(sec, 'promoHeading')) || (sec.data.promoBody && shows(sec, 'promoBody'))) {
                      <div class="promo">
                        @if (shows(sec, 'promoHeading')) { <h2 class="promo-h">{{ sec.data.promoHeading }}</h2> }
                        @if (shows(sec, 'promoBody')) { <div class="promo-b rich" [innerHTML]="rich(sec.data.promoBody)"></div> }
                      </div>
                    }
                  </div>
                }

                @case ('work') {
                  <div class="work">
                    <!-- Heading and description are optional and independent: the block only
                         appears if the artist wrote something, and each line only if it has text. -->
                    @if ((hasText(sec.data.heading) && shows(sec, 'heading')) || (hasText(sec.data.intro) && shows(sec, 'intro'))) {
                      <div class="wk-head" [ngClass]="'al-' + (sec.data.align || 'auto')">
                        @if (hasText(sec.data.heading) && shows(sec, 'heading')) { <h2 class="secttl">{{ sec.data.heading }}</h2> }
                        @if (hasText(sec.data.intro) && shows(sec, 'intro')) { <div class="wk-intro" [innerHTML]="sec.data.intro"></div> }
                      </div>
                    }
                    @if (worksFor(sec).length) {
                      <!-- ds-artwork-grid/card: square cell + object-fit:contain, so artwork is
                           never cropped or stretched. -->
                      <!-- The size control is the artist's, not the visitor's: hidden here, and the
                           size comes from the published design rather than the viewer's browser. -->
                      <ds-artwork-grid context="site-work" #wg class="d-block"
                        [showSizeControl]="false" [size]="content.card.size || 'md'">
                        @for (w of worksFor(sec); track w.id) {
                          <ds-artwork-card [size]="wg.size" [imageUrl]="w.image"
                            [name]="content.card.showTitle ? w.name : null"
                            [subtitle]="content.card.showCategory ? w.medium : null"
                            [price]="content.card.showPrice && w.price ? w.price : null" [currency]="currency"
                            [status]="content.card.showStatus ? statusOf(w) : null" [statusTone]="toneOf(w)"
                            [clickable]="true" (cardClick)="panel.openDetail(w)"></ds-artwork-card>
                        }
                      </ds-artwork-grid>
                    }
                  </div>
                }

                @case ('text') {
                  <div class="story">
                    @if (shows(sec, 'heading')) { <h2 class="secttl">{{ sec.data.heading }}</h2> }
                    @if (shows(sec, 'body')) { <div class="lede rich" [ngClass]="columnsClass(sec)" [innerHTML]="rich(sec.data.body)"></div> }
                  </div>
                }

                @case ('imageText') {
                  <div class="imgtext">
                    <div class="srow" [class.flip]="sec.data.flip" [class.no-img]="!shows(sec, 'image')">
                      @if (shows(sec, 'image')) {
                        <div class="rimg art">
                          @if (sec.data.image) { <img [src]="sec.data.image" [alt]="sec.data.heading || ''" /> }
                        </div>
                      }
                      <div>
                        @if (shows(sec, 'heading')) { <h3 class="rh">{{ sec.data.heading }}</h3> }
                        @if (shows(sec, 'body')) { <p>{{ sec.data.body }}</p> }
                      </div>
                    </div>
                  </div>
                }

                @case ('contact') {
                  <div class="contact">
                    @if (shows(sec, 'heading')) { <h2 class="secttl">{{ sec.data.heading }}</h2> }
                    @if (hasText(sec.data.intro) && shows(sec, 'intro')) { <div class="lede">{{ sec.data.intro }}</div> }

                    <div class="cgrid">
                      <div class="cdetails">
                        <!-- Actionable, not just printed: an email you can send to and a number you
                             can tap on a phone. Each line appears only if the artist filled it in. -->
                        @if (sec.data.email) {
                          <a class="cline" [href]="'mailto:' + sec.data.email"><span class="ci">✉</span>{{ sec.data.email }}</a>
                        }
                        @if (sec.data.phone) {
                          <a class="cline" [href]="'tel:' + telHref(sec.data.phone)"><span class="ci">☏</span>{{ sec.data.phone }}</a>
                        }
                        @if (sec.data.address) { <div class="cline"><span class="ci">⌂</span>{{ sec.data.address }}</div> }
                        @if (sec.data.location && !sec.data.address) { <div class="cline"><span class="ci">⌂</span>{{ sec.data.location }}</div> }

                        @if (socials(sec.data).length) {
                          <div class="csocial">
                            @for (s of socials(sec.data); track s.url) {
                              <a [href]="s.url" target="_blank" rel="noopener noreferrer nofollow" [title]="s.label">{{ s.label }}</a>
                            }
                          </div>
                        }
                      </div>

                      @if (sec.data.showForm !== false && sec.data.email) {
                        <form class="cform" (submit)="sendEnquiry($event)">
                          <label>
                            <span>Your name</span>
                            <input type="text" name="name" [(ngModel)]="enquiry.name" [ngModelOptions]="{standalone:true}" required />
                          </label>
                          <label>
                            <span>Your email</span>
                            <input type="email" name="email" [(ngModel)]="enquiry.email" [ngModelOptions]="{standalone:true}" required />
                          </label>
                          <label>
                            <span>Message</span>
                            <textarea name="message" rows="4" [(ngModel)]="enquiry.message" [ngModelOptions]="{standalone:true}" required></textarea>
                          </label>
                          <button type="submit" class="cbtn small" [disabled]="enquirySending">
                            {{ enquirySending ? 'Sending…' : 'Send enquiry' }}
                          </button>
                          @if (enquirySent) { <p class="cnote">Thank you — your message has been sent.</p> }
                          @if (enquiryError) {
                            <p class="cnote cnote-err">
                              {{ enquiryError }}
                              <!-- Their typed message is not lost: offer the direct route instead. -->
                              @if (mailtoFallback()) { <a [href]="mailtoFallback()">Send it by email instead</a> }
                            </p>
                          }
                        </form>
                      }
                    </div>
                  </div>
                }

              }

            </div>
          </div>
        }

        <!-- Attribution lives here and only here. A banner at the top would undo the point of the
             page: a visitor should feel they are on the artist's website, not inside ArtFlow. -->
        <div class="foot site-credit">
          <span class="cred-site">© {{ year }} {{ content.siteTitle }}</span>
          <a class="cred-cta" [href]="afpLink('footer')" target="_blank" rel="noopener noreferrer">
            <img src="assets/images/AFP-logo-horizontal/AFP-logo-free-artists-300.png" alt="ArtFlow Pro" />
            <span class="cred-text">
              <b>Create your own artist website — free</b>
              <small>Portfolio, galleries and sales tools, built for artists</small>
            </span>
          </a>
        </div>

      </div>
    </div>
  </div>

  <!-- ===== MODALS =====
       A visitor who wants a piece has to be able to ask about it from the grid. Without these the
       published site was a catalogue with no way to respond to it. -->
  <!-- The artwork panel and its enquiry form. Shared with anywhere else artwork is shown to the
       public, so the price, the sold badge and the piece reference cannot drift between them. -->
  <ds-artwork-enquiry #panel
                      [identifier]="identifier"
                      [currency]="currency"
                      [contactEmail]="contactData?.email"
                      [contextTitle]="content.siteTitle"></ds-artwork-enquiry>
</div>
